// =============================================================================
// VandieZ shipper-web -- My Vehicles (/vehicles)
// =============================================================================
// Lists vehicles the caller owns. Shippers that also wear the owner hat
// (their phone exists in `users` with `roles` array containing 'owner')
// onboard their fleet on mobile and manage it here.
//
// Onboarding a NEW vehicle stays on mobile because the backend requires 5
// KYC documents + 4+ photos uploaded through the KYC pipeline first; the
// camera + file picker UX is materially worse on web for low-end users.
// =============================================================================

import {
  CheckCircle2,
  Circle,
  Loader2,
  Smartphone,
  Truck,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { vehiclesApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { Vehicle, VehicleStatus } from "@/types/vehicle";

const STATUS_LABEL: Record<VehicleStatus, string> = {
  pending: "Pending review",
  active: "Active",
  rejected: "Rejected",
  maintenance: "Maintenance",
  suspended: "Suspended",
};

const STATUS_TONE: Record<VehicleStatus, string> = {
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  active: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-danger-subtle text-danger-subtle-foreground",
  maintenance: "bg-muted text-muted-foreground",
  suspended: "bg-danger-subtle text-danger-subtle-foreground",
};

export function VehiclesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<Vehicle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (accessToken === null) {return;}
    const controller = new AbortController();
    vehiclesApi
      .list(accessToken, { limit: 50, offset: 0 }, controller.signal)
      .then((res) => {
        setItems(res.items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load vehicles.",
        );
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="My vehicles"
        description="Edit settings, expiry dates, and toggle online availability."
      />

      <>
        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Card className="border-dashed">
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <Smartphone className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <CardTitle className="text-base">Add a new vehicle on mobile</CardTitle>
              <CardDescription>
                Onboarding needs 5 KYC documents and 4+ photos. Use the
                VandieZ mobile app to upload them, then come back here to
                manage settings, expiry dates, and online availability.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items === null || items.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Truck className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No vehicles yet</CardTitle>
              <CardDescription>
                Once you onboard a vehicle on the mobile app, it shows up
                here. You can then edit expiry dates, cargo bay dimensions,
                base location, and toggle it online for bidding.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((v) => (
              <Link
                key={v.id}
                to={`/vehicles/${v.id}`}
                className="block transition-colors hover:opacity-95"
              >
                <Card className="group cursor-pointer transition-colors hover:border-primary/50">
                  <CardContent className="flex items-center gap-3 py-4 sm:gap-4">
                    {v.primary_photo_url !== null ? (
                      <img
                        src={v.primary_photo_url}
                        alt={`${v.make} ${v.model}`}
                        className="h-14 w-20 rounded-md object-cover shrink-0 sm:h-16 sm:w-24"
                      />
                    ) : (
                      <div className="flex h-14 w-20 items-center justify-center rounded-md bg-muted shrink-0 sm:h-16 sm:w-24">
                        <Truck className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[v.status]}`}
                        >
                          {STATUS_LABEL[v.status]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {v.vehicle_type} · {v.fuel_type}
                        </span>
                      </div>
                      <p className="text-sm font-medium truncate">
                        {v.make} {v.model} · {v.year_of_manufacture}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground truncate">
                        {v.registration_number} · {v.payload_capacity_kg} kg
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="flex items-center justify-end gap-1 text-xs">
                        {v.online_for_bidding ? (
                          <>
                            <CheckCircle2 className="h-3 w-3 text-success" />
                            <span className="text-success-subtle-foreground">Online</span>
                          </>
                        ) : (
                          <>
                            <Circle className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Offline</span>
                          </>
                        )}
                      </p>
                      {v.vahan_verified ? (
                        <p className="text-xs text-muted-foreground">
                          Vahan ✓
                        </p>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </>
    </div>
  );
}
